import { useCallback } from 'react';
import { getIn, useFormikContext } from 'formik';
import { matchSorter } from 'match-sorter';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

import { invariant } from '@e-socna/utils';
import { ThirdPartyTypeEnum } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { formatOrganizationIdentifierValue } from '../../../../utils/format-organization-identifier.util';
import type { ThirdPartiesCmp_Person } from '../../../../components/ThirdParties/fragments/person.fragment';
import type { ThirdPartiesCmp_Organization } from '../../../../components/ThirdParties/fragments/organization.fragment';

import { UserRolesFormControl } from '../../../UserRoles';
import { RepresentativeTypesFormControl } from '../../../RepresentativeTypes';

import type { ThirdPartyForm_Organization } from '../../fragments/organization.fragment';
import type { ThirdPartyForm_Person } from '../../fragments/person.fragment';

interface Props {
  readonly index: number;
  readonly isInternal: boolean;
  readonly kind: ThirdPartyTypeEnum;
  readonly organization?: ThirdPartiesCmp_Organization;
  readonly organizationMembership?: ThirdPartyForm_Organization['activeMemberships'][number];
  readonly person?: ThirdPartiesCmp_Person;
  readonly personMembership?: ThirdPartyForm_Person['activeMemberships'][number];
}

export const MembershipDetails = ({
  index,
  isInternal,
  kind,
  organization,
  organizationMembership,
  person,
  personMembership,
}: Readonly<Props>) => {
  invariant(
    kind !== ThirdPartyTypeEnum.Organization ||
      !!(person && organizationMembership),
    'person and organizationMembership must be defined if kind is Organization',
  );

  invariant(
    kind !== ThirdPartyTypeEnum.Person || !!(organization && personMembership),
    'organization and personMembership must be defined if kind is Person',
  );

  const { organizationFunctions } = useReferencesContext();

  const { errors, handleBlur, setFieldValue, touched } = useFormikContext();

  const id =
    (kind === ThirdPartyTypeEnum.Organization
      ? person?._id
      : organization?._id) ?? index;

  const identifierValue = formatOrganizationIdentifierValue(
    organization?.identifier,
  );

  const name =
    kind === ThirdPartyTypeEnum.Organization && person
      ? `${person.firstName ? `${person.firstName} ` : EMPTY_STRING}${person.lastName}`
      : kind === ThirdPartyTypeEnum.Person && organization
        ? `${identifierValue ? `${identifierValue} - ` : EMPTY_STRING}${organization.name}`
        : 'Tiers Inconnu';

  const functions =
    (kind === ThirdPartyTypeEnum.Organization
      ? organizationMembership?.functions
      : personMembership?.functions) ?? EMPTY_ARRAY;

  const indexAsString = index.toFixed();

  const handleChange = useCallback(
    (_e: unknown, value: readonly string[]) => {
      void setFieldValue(
        `activeMemberships[${indexAsString}].functions`,
        value,
      );
    },
    [indexAsString, setFieldValue],
  );

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        error={
          !!(
            getIn(errors, `activeMemberships[${indexAsString}].functions`) &&
            getIn(touched, `activeMemberships[${indexAsString}].functions`)
          )
        }
        fullWidth
        helperText={
          getIn(
            errors,
            `activeMemberships[${indexAsString}].functions`,
          ) as string
        }
        id={`active-memberships-${indexAsString}-functions`}
        label="Fonctions"
        name={`activeMemberships[${indexAsString}].functions`}
        onBlur={handleBlur}
        placeholder="Ajouter"
      />
    ),
    [errors, handleBlur, indexAsString, touched],
  );

  const filterOptions = useCallback(
    (
      options: readonly string[],
      { inputValue }: Readonly<{ inputValue: string }>,
    ) => matchSorter(options, inputValue),
    [],
  );

  return (
    <Paper key={id} sx={{ width: '100%', p: 2 }}>
      <Typography sx={{ mb: 4 }} variant="h6">
        {name}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <Autocomplete<string, true, false, true>
            filterOptions={filterOptions}
            freeSolo
            id={`active-memberships-${indexAsString}-functions-autocomplete`}
            multiple
            onChange={handleChange}
            onOpen={handleBlur}
            options={organizationFunctions.map(
              (organizationFunction) => organizationFunction.label,
            )}
            renderInput={renderInput}
            slotProps={{ chip: { size: 'small', variant: 'outlined' } }}
            value={[...functions]}
          />
        </Grid>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <UserRolesFormControl
            fallbackRoleIds={undefined}
            name={`activeMemberships[${indexAsString}].contextualRoleIds`}
            required={false}
            scope="contextual"
          />
        </Grid>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <RepresentativeTypesFormControl
            fallbackTypeIds={undefined}
            isInternal={isInternal}
            name={`activeMemberships[${indexAsString}].representativeTypeIds`}
            required={false}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
